import React, { useEffect, useMemo, useState } from "react";
import * as api from "../api";
import { audioUrl, exportRatingExcelSilent } from "../api";
import AudioPlayer from "../components/AudioPlayer";
import AgentDropdown from "../components/AgentDropdown";


async function loadTemplateDoc(templateId, typeHint = "online") {
  if (templateId) {
    try {
      const all = await api.listTemplates();
      const t = all.find((x) => x.id === templateId);
      if (t) return t;
    } catch (e) {
      console.warn("listTemplates failed; will try default by type", e);
    }
  }
  try {
    const def = await api.getDefaultTemplate(typeHint);
    if (def?.id) return def;
  } catch (e) {
    console.warn("getDefaultTemplate failed", e);
  }
  try {
    const all = await api.listTemplates();
    return all?.[0] || null;
  } catch (e) {
    console.error("loadTemplateDoc: fallback listTemplates failed", e);
    return null;
  }
}

export default function RatingsPage({ fileId, onSaved }) {
  const [audioSrc, setAudioSrc] = useState(null);
  const [audioErr, setAudioErr] = useState(null);
  const [sections, setSections] = useState([]);
  const [templateId, setTemplateId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [ratingUsage, setRatingUsage] = useState(null);
  const [selectedOperator, setSelectedOperator] = useState({ id: "", name: "" });
  const [showComment, setShowComment] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [showSummary, setShowSummary] = useState(false);
  const [summaryText, setSummaryText] = useState("");
  const [whyMap, setWhyMap] = useState({});
  const [evMatched, setEvMatched] = useState({});
  const [evWhyUnmatched, setEvWhyUnmatched] = useState({});
  const [openEvidence, setOpenEvidence] = useState(null);

  
  const operatorLocked = true;


  const SEC_LATIN = { "А": "A", "Б": "B", "В": "C", "Г": "G" };
  function latinizeItemId(itemId = "") {
    const secCyr = itemId?.[0] || "";
    const num = itemId?.slice(1) || "";
    return (SEC_LATIN[secCyr] || secCyr) + num;
  }
  function keyVariants(itemId = "") {
    const lat = latinizeItemId(itemId);
    return lat === itemId ? [itemId] : [itemId, lat];
  }
  function mapGet(mapObj, itemId) {
    if (!mapObj) return undefined;
    for (const k of keyVariants(itemId)) {
      if (mapObj[k] != null) return mapObj[k];
    }
    return undefined;
  }
  function asText(val) {
    if (val == null) return "";
    if (typeof val === "string") return val;
    if (Array.isArray(val)) return val.map(asText).join(", ");
    if (typeof val === "object") {
      if (Object.keys(val).length === 0) return "";
      return val.text ?? val.reason ?? val.message ?? JSON.stringify(val);
    }
    return String(val);
  }
  function splitKinds(entry) {
    if (Array.isArray(entry)) return { must: entry, guide: [], standards: [] };
    if (entry && typeof entry === "object") {
      return {
        must: Array.isArray(entry.must) ? entry.must : [],
        guide: Array.isArray(entry.guide) ? entry.guide : [],
        standards: Array.isArray(entry.standards) ? entry.standards : [],
      };
    }
    return { must: [], guide: [], standards: [] };
  }

  function whyForItem(itemId) {
    if (!itemId) return "";
    const latin = latinizeItemId(itemId);
    return (whyMap && (whyMap[latin] || whyMap[itemId])) || "";
  }
  function sectionDisplayTitle(sec) {
    const id = String(sec?.id ?? "").trim();
    const title = String(sec?.title ?? "").trim();
    if (!id) return title || "";
    const prefix = `${id})`;
    if (title.toUpperCase().startsWith(prefix.toUpperCase())) return title;
    return title ? `${prefix} ${title}` : prefix;
  }

  function hasKeys(o) {
    return o && typeof o === "object" && !Array.isArray(o) && Object.keys(o).length > 0;
  }

  function PhraseCard({ text, kind, compact = false }) {
    const base =
      (compact ? "inline-block max-w-full" : "w-full") +
      " rounded-xl border px-3 py-2 text-sm leading-5";
    const toned =
      kind === "guide"
        ? "bg-sky-900/10 border-sky-700/30 text-sky-100"
        : kind === "standard"
          ? "bg-violet-900/10 border-violet-700/30 text-violet-100"
          : "bg-emerald-900/10 border-emerald-700/30 text-emerald-100";
    return <div className={`${base} ${toned}`}>{asText(text)}</div>;
  }

  useEffect(() => {
    let alive = true;
    setAudioSrc(null);
    setAudioErr(null);
    if (!fileId) return;
    (async () => {
      try {
        const { url } = await audioUrl(fileId);
        if (alive) setAudioSrc(url);
      } catch (e) {
        console.error(e);
        if (alive) setAudioErr("Няма аудио или няма достъп.");
      }
    })();
    return () => {
      alive = false;
    };
  }, [fileId]);

  useEffect(() => {
    if (!fileId) return;
    (async () => {
      try {
        const saved = await api.getRating(fileId);
        const tplId = saved?.template_id || null;
        setTemplateId(tplId);

        const tpl = await loadTemplateDoc(tplId);

        const answers = saved?.answers || {};
        const built =
          (tpl?.sections || []).map((s) => ({
            id: s.id,
            title: s.title || "",
            open: false,
            items: (s.items || []).map((it) => {
              const raw = answers[it.id];
              const n = Number(raw);
              return {
                id: it.id,
                label: it.label || "",
                points: Number.isFinite(n) && n > 0 ? Math.max(1, Math.min(5, n)) : "",
              };
            }),
          })) || [];

        setSections(built);

        setWhyMap((saved?.meta && saved.meta.why) || {});
        setEvMatched((saved?.meta && saved.meta.matched) || {});
        setEvWhyUnmatched((saved?.meta && saved.meta.why_unmatched) || {});

        setRatingUsage(saved?.usage || null);
        const n = saved?.notes || {};
        const comm = n.comment || "";
        const summ = n.summary || "";
        setSelectedOperator({
          id: n.operator_id || "",
          name: n.operator_name || "",
        });

        setCommentText(comm);
        setShowComment(!!comm);
        setSummaryText(summ);
        setShowSummary(!!summ);
      } catch (e) {
        console.error("Ratings load failed", e);
      }
    })();
  }, [fileId]);

  const totals = useMemo(() => {
    const bySection = (sections || []).map((s) => {
      const max = (s.items?.length || 0) * 5;
      const sum = (s.items || []).reduce((a, b) => a + (Number(b.points) || 0), 0);
      return { id: s.id, max, sum };
    });

    const overallMax = bySection.reduce((a, b) => a + b.max, 0);
    const overallSum = bySection.reduce((a, b) => a + b.sum, 0);

    return { bySection, overallMax, overallSum };
  }, [sections]);


  const updateSelection = (sid, iid, val) => {
    setSections((prev) =>
      prev.map((s) =>
        s.id === sid
          ? {
            ...s,
            items: s.items.map((it) =>
              it.id === iid
                ? { ...it, points: val === "" ? "" : Number(val) }
                : it
            ),
          }
          : s
      )
    );
  };

  const buildSavePayload = () => {
    const answers = {};
    sections.forEach((s) =>
      (s.items || []).forEach((it) => {
        const n = Number(it.points);
        answers[it.id] = Number.isFinite(n) ? Math.max(1, Math.min(5, n)) : 0;
      })
    );
    return {
      template_id: templateId,
      answers,
      notes: {
        operator_id: selectedOperator?.id || "",
        operator_name: selectedOperator?.name || "",
        comment: commentText || "",
        summary: summaryText || "",
      },
    };
  };

  const handleSave = async () => {
    if (!fileId) return;
    if (!templateId) {
      alert("Липсва template_id за този рейтинг.");
      return;
    }
    try {
      setLoading(true);
      await api.saveRating(fileId, buildSavePayload());
      await exportRatingExcelSilent(fileId);
      onSaved?.();
      alert("Запазено!");
    } catch (e) {
      console.error(e);
      alert("Грешка при запис.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card rounded-2xl p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold">Оценки</h2>
        {ratingUsage && (ratingUsage.input > 0 || ratingUsage.output > 0) && (
          <div className="flex items-center gap-1">
            <span
              className="px-2 py-0.5 rounded-full bg-[#121825] border border-[#1f2638] text-[11px] leading-none text-slate-300"
              title="Входни токени (prompt)"
            >
              in {ratingUsage.input}
            </span>
            <span
              className="px-2 py-0.5 rounded-full bg-[#121825] border border-[#1f2638] text-[11px] leading-none text-slate-300"
              title="Изходни токени (completion)"
            >
              out {ratingUsage.output}
            </span>
            <span
              className="px-2 py-0.5 rounded-full bg-[#121825] border border-violet-700/40 text-[11px] leading-none text-slate-200 font-semibold"
              title="Общо токени"
            >
              tot {ratingUsage.total}
            </span>
          </div>
        )}
      </div>

      <div className="mb-4">
        {audioSrc ? (
          <AudioPlayer src={audioSrc} className="w-full" />
        ) : (
          <div className="rounded-xl border border-[#1f2638] bg-[#0e1320]/40 p-3 text-sm text-slate-400">
            {audioErr || "Зареждане на аудио…"}
          </div>
        )}
      </div>

      <div className="grid grid-cols-[20px,1fr,90px,80px] items-center px-4 py-2 text-sm font-semibold text-slate-300 bg-[#0e1320] rounded-t-2xl">
        <div></div>
        <div>Критерии</div>
        <div className="text-right">Точки</div>
        <div className="text-center">Оценка</div>
      </div>

      {sections.map((sec) => (
        <div key={sec.id} className="border-t border-[#1f2638]">
          <div
            className="grid grid-cols-[20px,1fr,90px,80px] items-center px-4 pt-3 pb-1 bg-[#0e1320]/40 cursor-pointer hover:bg-[#0e1320]/60"
            onClick={() =>
              setSections((prev) =>
                prev.map((s) =>
                  s.id === sec.id ? { ...s, open: !s.open } : s
                )
              )
            }
          >
            <div className="justify-self-start text-slate-400">
              <svg
                className={`w-4 h-4 transition-transform ${sec.open ? "rotate-90" : ""}`}
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path d="M7 5l6 5-6 5V5z" />
              </svg>
            </div>
            <div className="font-semibold">{sectionDisplayTitle(sec)}</div>

            <div className="text-right">
              {(() => {
                const t = totals.bySection.find((x) => x.id === sec.id);
                return `${t?.sum || 0} / ${t?.max || 0}`;
              })()}
            </div>
            <div className="text-center font-semibold text-slate-300"></div>

          </div>

          {sec.open && (
            <div className="divide-y divide-[#1f2638]">
              {sec.items.map((it) => (
                <div
                  key={it.id}
                  className="grid grid-cols-[20px,1fr,90px,80px] items-center px-4 py-3"
                  onClick={() => setOpenEvidence(cur => cur === it.id ? null : it.id)}
                >
                  <div></div>

                  <div className="text-slate-200">
                    <span className="relative group inline-block tabular-nums">
                      <span
                        className="cursor-help focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-500/50 rounded-sm"
                        tabIndex={0}
                      >
                        {it.label}
                      </span>

                      {!!whyForItem(it.id) && (
                        <span
                          role="tooltip"
                          className="
                            pointer-events-none absolute -top-2 left-0 -translate-y-full z-50
                            invisible opacity-0 group-hover:visible group-hover:opacity-100
                            group-focus-within:visible group-focus-within:opacity-100
                            transition-opacity duration-150
                            bg-[#0b1220] border border-[#1f2638] text-slate-200 text-xs
                            rounded-xl px-3 py-2 shadow-xl
                            min-w-[220px] md:min-w-[220px] max-w-[460px]
                            whitespace-pre-line break-words
                          "
                        >
                          {whyForItem(it.id)}
                          <span
                            aria-hidden="true"
                            className="
                              absolute -bottom-1 left-4 w-2 h-2 rotate-45
                              bg-[#0b1220] border-b border-r border-[#1f2638]
                            "
                          />
                        </span>
                      )}
                    </span>
                  </div>

                  <div className="text-right">
                    {`${Number(it.points || 0)} / 5`}
                  </div>

                  <div className="text-center" onClick={(e) => e.stopPropagation()}>
                    <select
                      value={it.points === "" ? "" : Number(it.points)}
                      className="bg-[#121825] border border-[#1f2638] rounded-lg px-2 py-1 text-sm outline-none"
                      onChange={(e) =>
                        updateSelection(
                          sec.id,
                          it.id,
                          e.target.value === "" ? "" : Number(e.target.value)
                        )
                      }
                      disabled={loading}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <option value="" disabled hidden></option>
                      <option value={1}>1</option>
                      <option value={2}>2</option>
                      <option value={3}>3</option>
                      <option value={4}>4</option>
                      <option value={5}>5</option>
                    </select>
                  </div>
                  {openEvidence === it.id && (
                    <div className="col-span-4 mt-2 mb-3 ml-5 mr-4">
                      <div className="rounded-2xl p-4 ring-1 ring-[#1f2638] bg-gradient-to-br from-[#0b1220]/70 to-[#0f1629]/70 shadow-lg shadow-black/30">

                        {(() => {
                          const m = splitKinds(mapGet(evMatched, it.id));
                          const whyObjRaw = mapGet(evWhyUnmatched, it.id);

                          const hasMatched =
                            (m.must?.length || 0) + (m.guide?.length || 0) + (m.standards?.length || 0) > 0;
                          const hasWhyList = hasKeys(whyObjRaw);
                          const hasWhyText = !hasWhyList && typeof whyObjRaw === "string" && whyObjRaw.trim() !== "";
                          const showWhyCol = hasWhyList || hasWhyText;

                          const cols = hasMatched && showWhyCol ? "md:grid-cols-2" : "md:grid-cols-1";

                          if (!hasMatched && !showWhyCol) {
                            return (
                              <div className="text-xs text-slate-500">
                                Няма налични детайли за този критерий (всички фрази са покрити или липсват обяснения).
                              </div>
                            );
                          }

                          return (
                            <div className={`grid gap-4 ${cols}`}>
                              {hasMatched && (
                                <div className="space-y-2">
                                  <div className="text-xs uppercase tracking-wide text-slate-400">
                                    Покрити елементи
                                  </div>

                                  <div className={showWhyCol ? "space-y-2" : "flex flex-wrap gap-2"}>
                                    {m.must.map((t, i) => (
                                      <PhraseCard key={`m-m-${i}`} text={t} kind="must" compact={!showWhyCol} />
                                    ))}
                                    {m.guide.map((t, i) => (
                                      <PhraseCard key={`m-g-${i}`} text={t} kind="guide" compact={!showWhyCol} />
                                    ))}
                                    {m.standards.map((t, i) => (
                                      <PhraseCard key={`m-s-${i}`} text={t} kind="standard" compact={!showWhyCol} />
                                    ))}
                                    {m.must.length === 0 && m.guide.length === 0 && m.standards.length === 0 && (
                                      <div className="text-xs text-slate-500">няма данни</div>
                                    )}
                                  </div>
                                </div>
                              )}

                              {showWhyCol && (
                                <div className="space-y-2">
                                  <div className="text-xs uppercase tracking-wide text-slate-400">Обяснения за пропуснатите</div>

                                  {hasWhyList && (
                                    <div className="rounded-xl p-2 space-y-2">
                                      {Object.entries(whyObjRaw).map(([phrase, reason], idx) => (
                                        <div key={`why-${idx}`} className="rounded-lg bg-[#0f1629] border border-[#263350] p-2">
                                          <div className="text-sm text-slate-200 font-medium">{asText(phrase)}</div>
                                          <div className="text-xs text-slate-400 mt-1 whitespace-pre-line break-words">
                                            {asText(reason)}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  )}

                                  {!hasWhyList && hasWhyText && (
                                    <div className="rounded-xl bg-[#0e1320] border border-[#1f2638] p-3 text-xs text-slate-200 whitespace-pre-line break-words">
                                      {asText(whyObjRaw)}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  )}

                </div>
              ))}
            </div>
          )}
        </div>
      ))}

      <div className="mt-4 rounded-2xl border border-[#1f2638] bg-[#0e1320]/50">
        <div className="p-4 grid gap-6 md:grid-cols-2">
          <div className="space-y-2">
            <label className="text-sm font-semibold text-slate-300">
              Оператор
            </label>

            <AgentDropdown
              value={selectedOperator}
              onChange={(agent) => {
                if (operatorLocked) return;
                setSelectedOperator(agent);
              }}
              disabled={operatorLocked}
            />
            <p className="text-xs text-slate-500 mt-1">
              Операторът се задава при качване на разговора и не може да се променя от този екран.
            </p>
          </div>
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  className="form-checkbox h-4 w-4 rounded border-[#1f2638] bg-[#121825] text-violet-500"
                  checked={showComment}
                  onChange={(e) => setShowComment(e.target.checked)}
                />
                <span className="text-sm text-slate-300">Коментар</span>
              </label>

              {showComment && (
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-slate-400">Кратък коментар към оценката</span>
                    <span className="text-[11px] text-slate-500 tabular-nums">
                      {commentText.length} знака
                    </span>
                  </div>
                  <textarea
                    rows={3}
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    placeholder="Напр. конкретни забележки, контекст, препоръки…"
                    className="w-full resize-y rounded-lg bg-[#121825] border border-[#1f2638] px-3 py-2 text-sm text-slate-200 outline-none
                         focus:ring-2 focus:ring-violet-600/40 nice-scrollbar"
                  />
                </div>
              )}
            </div>
            <div className="space-y-2">
              <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  className="form-checkbox h-4 w-4 rounded border-[#1f2638] bg-[#121825] text-violet-500"
                  checked={showSummary}
                  onChange={(e) => setShowSummary(e.target.checked)}
                />
                <span className="text-sm text-slate-300">Обобщение</span>
              </label>

              {showSummary && (
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-slate-400">Обобщение</span>
                    <span className="text-[11px] text-slate-500 tabular-nums">
                      {summaryText.length} знака
                    </span>
                  </div>
                  <textarea
                    rows={3}
                    value={summaryText}
                    onChange={(e) => setSummaryText(e.target.value)}
                    placeholder="Кратко обобщение (напр. 2–3 изречения)…"
                    className="w-full resize-y rounded-lg bg-[#121825] border border-[#1f2638] px-3 py-2 text-sm text-slate-200 outline-none
                         focus:ring-2 focus:ring-violet-600/40 nice-scrollbar"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center">
        <div className="rounded-2xl border border-[#1f2638] bg-[#0e1320]/60 px-3 py-2 text-sm">
          Общо: <span className="font-semibold">{totals.overallSum}</span> / {totals.overallMax}
        </div>

        <div className="sm:ml-auto flex gap-2">
          <button className="btn btn-primary" onClick={handleSave} disabled={loading || !fileId}>
            {loading ? "Запис…" : "Запази"}
          </button>
        </div>
      </div>
    </div>
  );
}
